import { defineCrestModule } from '@crest/platform-contracts/modules';
import { platformOptionalFactProviderHostCapability } from '@crest/platform-contracts/hosts';
import { trainingActivityCapability, trainingActivitySummaryCapability, } from './contracts/training-activity.contract.js';
import { trainingTimesheetSummaryHostCapability, trainingTimesheetSummaryHostFallbackCapability, } from './contracts/training-timesheet-summary-host.contract.js';
import { trainingWorkforceProfileHostCapability, trainingWorkforceProfileHostFallbackCapability, } from './contracts/training-workforce-profile-host.contract.js';
import { trainingWorkforceProfileStatusCapability } from './contracts/training-workforce-profile-status.contract.js';
import { trainingAssessmentsCapability, trainingGradesCapability } from './contracts/training-assessment.contract.js';
import { trainingCatalogCapability, trainingCitationsCapability, trainingContentReleasesCapability, } from './contracts/training-content.contract.js';
import { trainingAssignmentsCapability, trainingProgressCapability } from './contracts/training-learning.contract.js';
import { trainingCertificationsCapability } from './contracts/training-certification.contract.js';
import { trainingImportCapability } from './contracts/training-import.contract.js';
import { trainingReportingTeamSummaryCapability, trainingReportingTeamSummaryProviderId, } from './contracts/training-reporting-team-summary.contract.js';
import { trainingDocumentsRecordFactsCapability, trainingDocumentsRecordFactsProviderId, } from './contracts/training-documents-record-facts.contract.js';
import { trainingContentPackagesCapability } from './content-packages/training-content-package.capability.js';
import { trainingAssessmentResultNotificationCapability } from './contracts/training-assessment-result-notification.contract.js';
import { trainingKnowledgePublishedArtifactCapability, trainingKnowledgePublishedReferenceCapability, } from './contracts/training-knowledge-published-artifact.contract.js';
export const trainingModuleManifest = defineCrestModule({
    id: 'training', version: '1.0.0', kind: 'business', required: false,
    requires: ['platform'],
    requiredCapabilities: [
        'platform.tenant-context.v1', 'platform.module-access.v1', 'platform.membership-directory.v1',
        platformOptionalFactProviderHostCapability,
    ],
    optionalCapabilities: [
        trainingTimesheetSummaryHostCapability,
        trainingWorkforceProfileHostCapability,
        trainingKnowledgePublishedArtifactCapability,
    ],
    optionalCapabilityFallbacks: [
        {
            capability: trainingTimesheetSummaryHostCapability,
            degradedMode: trainingTimesheetSummaryHostFallbackCapability,
        },
        {
            capability: trainingWorkforceProfileHostCapability,
            degradedMode: trainingWorkforceProfileHostFallbackCapability,
        },
        {
            capability: trainingKnowledgePublishedArtifactCapability,
            degradedMode: 'training.degraded.retained-knowledge-references-only.v1',
        },
    ],
    provides: [
        trainingCatalogCapability, trainingContentReleasesCapability, trainingAssignmentsCapability,
        trainingProgressCapability, trainingAssessmentsCapability, trainingGradesCapability,
        trainingCitationsCapability, trainingActivityCapability, trainingActivitySummaryCapability,
        trainingContentPackagesCapability,
        trainingCertificationsCapability, trainingImportCapability,
        trainingWorkforceProfileStatusCapability,
        trainingReportingTeamSummaryCapability,
        trainingDocumentsRecordFactsCapability,
        trainingAssessmentResultNotificationCapability,
        trainingKnowledgePublishedReferenceCapability,
    ],
    permissions: [
        'training.catalog.read', 'training.learning.complete', 'training.assignments.read-own',
        'training.assignments.manage', 'training.grades.read-own', 'training.grades.read-all',
        'training.content.manage', 'training.activity.read-private',
        'training.content-packages.read', 'training.content-packages.execute',
        'training.content-packages.rollback', 'training.content-signing-keys.manage',
        'training.certifications.read-own', 'training.certifications.read-all',
        'training.certifications.manage',
        'training.profile-status.read-all',
        'training.reporting.read-all',
        'training.documents.read-all',
        'training.import.read', 'training.import.execute', 'training.import.rollback',
    ],
    settings: [
        'training.learning.configuration', 'training.activity.configuration', 'training.display.center',
        'training.display.management',
        'training.display.timesheet-summary',
        'training.display.workforce-profile-status',
        'training.copy.learning', 'training.copy.management',
        'training.certifications.configuration', 'training.copy.certifications',
        'training.copy.timesheet-summary',
        'training.copy.workforce-profile-status',
    ],
    copyCatalogs: [
        'training.copy.learning', 'training.copy.management', 'training.copy.certifications',
        'training.copy.timesheet-summary',
        'training.copy.workforce-profile-status',
    ],
    events: {
        publishes: [
            'training.course-created.v1', 'training.release-created.v1', 'training.release-published.v1',
            'training.course-archived.v1', 'training.assignment-created.v1', 'training.assignment-waived.v1',
            'training.lesson-completed.v1', 'training.course-completed.v1',
            'training.assessment-started.v1', 'training.assessment-submitted.v1',
            'training.assessment-result-notification-requested.v1',
            'training.activity-started.v1', 'training.activity-closed.v1',
            'training.certification-recorded.v1', 'training.certification-renewed.v1',
            'training.certification-revoked.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'training.routes.catalog', requiredPermission: 'training.catalog.read', priority: 0 },
            { id: 'training.routes.learning', requiredPermission: 'training.learning.complete', priority: 1 },
            { id: 'training.routes.assessment', requiredPermission: 'training.learning.complete', priority: 2 },
            { id: 'training.routes.management', requiredPermission: 'training.content.manage', priority: 3 },
            { id: 'training.routes.activity-management', requiredPermission: 'training.activity.read-private', priority: 4 },
            { id: 'training.routes.certifications', requiredPermission: 'training.certifications.read-own', priority: 5 },
            { id: 'training.routes.workforce-profile-status', requiredPermission: 'training.profile-status.read-all', priority: 6 },
        ],
        jobs: [], subscribers: [], slots: [{
                id: trainingReportingTeamSummaryProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'training.reporting.read-all',
                priority: 70,
            }, {
                id: trainingDocumentsRecordFactsProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'training.documents.read-all',
                priority: 80,
            }],
    },
    web: {
        routes: [
            { id: 'training.web.center', requiredPermission: 'training.catalog.read', priority: 0 },
            { id: 'training.web.course', requiredPermission: 'training.catalog.read', priority: 1 },
            { id: 'training.web.assessment', requiredPermission: 'training.learning.complete', priority: 2 },
            { id: 'training.web.management', requiredPermission: 'training.content.manage', priority: 3 },
            { id: 'training.web.grades', requiredPermission: 'training.grades.read-all', priority: 4 },
            { id: 'training.web.certifications', requiredPermission: 'training.certifications.read-own', priority: 5 },
        ],
        navigation: [
            { id: 'training.navigation.center', requiredPermission: 'training.catalog.read', priority: 82 },
            { id: 'training.navigation.management', requiredPermission: 'training.content.manage', priority: 83 },
            { id: 'training.navigation.grades', requiredPermission: 'training.grades.read-all', priority: 84 },
            { id: 'training.navigation.certifications', requiredPermission: 'training.certifications.read-own', priority: 85 },
        ],
        slots: [{
                id: 'training.web.timesheets.activity-summary',
                requiredCapability: trainingTimesheetSummaryHostCapability,
                requiredPermission: 'training.activity.read-private',
                priority: 50,
            }, {
                id: 'training.web.workforce.profile-status',
                requiredCapability: trainingWorkforceProfileHostCapability,
                requiredPermission: 'training.profile-status.read-all',
                priority: 60,
            }],
    },
    mobile: { routes: [], slots: [] }, portal: { routes: [], slots: [] },
    database: {
        schema: 'training',
        owns: [
            'training.courses', 'training.course_releases', 'training.course_lifecycle_events',
            'training.assignments', 'training.assignment_events', 'training.progress',
            'training.progress_events', 'training.assessment_attempts', 'training.activity_sessions',
            'training.commands', 'training.execute_course_command', 'training.execute_assignment_command',
            'training.complete_lesson', 'training.start_assessment', 'training.submit_assessment',
            'training.execute_activity_command', 'training.content_signing_keys',
            'training.content_package_runs', 'training.content_package_receipts',
            'training.content_package_rejects', 'training.content_package_rollback_receipts',
            'training.register_content_signing_key', 'training.revoke_content_signing_key',
            'training.get_content_signing_key', 'training.list_content_signing_keys',
            'training.begin_content_package_run', 'training.record_content_package_reject',
            'training.record_content_package_receipt', 'training.complete_content_package_run',
            'training.fail_content_package_run', 'training.mark_content_package_run_rolled_back',
            'training.get_content_package_run_evidence',
            'training.certifications', 'training.certification_events',
            'training.execute_certification_command',
            'training.import_runs', 'training.import_identity_map',
            'training.import_row_receipts', 'training.import_rejects',
            'training.import_rollback_receipts', 'training.begin_import_run',
            'training.record_import_reject', 'training.apply_import_action',
            'training.complete_import_run', 'training.fail_import_run',
            'training.rollback_import_run', 'training.get_import_run',
            'training.get_import_run_evidence',
        ],
        readsThrough: [
            'platform.membership-directory.v1',
            trainingKnowledgePublishedArtifactCapability,
        ],
        migrations: [
            'training.foundation', 'training.content-package-reconciliation',
            'training.certifications-foundation',
            'training.import-reconciliation',
            'training.workforce-profile-status',
            'training.reporting-team-summary',
            'training.documents-record-facts',
            'training.knowledge-published-artifact-references',
        ],
    },
    health: ['training.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 96, jobConcurrency: 0, queueBacklogLimit: 0,
            databaseConnectionBudget: 12, workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain', clientContributions: 'remove', serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe', durableCursor: false, retentionDays: 0, backlogLimit: 0, warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'The first Training slice publishes retained facts but has no subscribers or delivery worker.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send', inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published', healthChecks: ['training.database'],
            reconciliationProcedure: null, visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove', data: 'retain', tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Training learning, professional credential history, content-package manifests, reconciliation receipts, and commands remain while disabled.',
});
//# sourceMappingURL=training.module.js.map